package fr.manyverse.app.domain.usecase

import fr.manyverse.app.domain.model.ActiveDigicode
import fr.manyverse.app.domain.model.EventContent
import fr.manyverse.app.domain.model.EventType
import fr.manyverse.app.domain.repository.DigicodeRepository
import fr.manyverse.app.domain.repository.GameEventRepository
import fr.manyverse.app.domain.validation.DigicodeValidator

/**
 * Use case for creating a digicode challenge.
 * Validates inputs, creates the challenge, and logs a DIGICODE_LAUNCHED event.
 */
class CreateDigicodeUseCase(
    private val digicodeRepository: DigicodeRepository,
    private val gameEventRepository: GameEventRepository
) {
    suspend operator fun invoke(
        tableId: String,
        targetParticipantId: String,
        digitCount: Int,
        maxAttempts: Int,
        timeLimitSeconds: Int?,
        showAttemptsToPlayer: Boolean,
        soundEnabled: Boolean,
        solution: String,
        createdBy: String
    ): Result<ActiveDigicode> {
        DigicodeValidator.validateForCreation(
            targetParticipantId = targetParticipantId,
            digitCount = digitCount,
            maxAttempts = maxAttempts,
            timeLimitSeconds = timeLimitSeconds,
            solution = solution
        ).onFailure {
            return Result.failure(it)
        }

        val result = digicodeRepository.createDigicode(
            tableId = tableId,
            targetParticipantId = targetParticipantId,
            digitCount = digitCount,
            maxAttempts = maxAttempts,
            timeLimitSeconds = timeLimitSeconds,
            showAttemptsToPlayer = showAttemptsToPlayer,
            soundEnabled = soundEnabled,
            solution = solution,
            createdBy = createdBy
        )

        result.onFailure {
            return Result.failure(it)
        }

        val digicode = result.getOrThrow()

        val content = EventContent.DigicodeLaunched(
            digicodeId = digicode.id,
            digitCount = digitCount
        )

        gameEventRepository.createEvent(
            tableId = tableId,
            type = EventType.DIGICODE_LAUNCHED,
            triggeredBy = createdBy,
            targetParticipants = listOf(targetParticipantId),
            content = content
        )

        return Result.success(digicode)
    }
}
